import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import Select from 'react-select'
import moment from 'moment'
import { useNavigate } from 'react-router-dom'
import { get, post } from '../../../../utils/Api'
import { useGlobalContext } from '../../../../utils/Context'
import FormLoader from '../../../../components/loader/FormLoader'
import NewAutoComplete from '../../../../components/autoComplete/NewAutoComplete'
import StateData from '../../../../assets/data/StateData'
import './QuickQuote.scss'

const STATE_NAMES: string[] = StateData.map((data: any) => data.state.name)

// Snap a free-text/autocomplete state value onto a real Nigerian state name —
// domestic pricing zones are classified by state, so anything else must not
// reach the quote payload.
const snapToState = (raw: string): string => {
  const n = (raw || '')
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/ state$/, '')
  if (n === 'abuja' || n === 'federal capital territory') return 'FCT'
  return STATE_NAMES.find((s) => s.toLowerCase() === n) || ''
}

type Pkg = {
  weight: number | string
  length: number | string
  width: number | string
  height: number | string
}

type Address = {
  countryCode: string
  cityName: string
  countyName: string
  postalCode: string
  addressLine1: string
}

const emptyAddress = (countryCode = ''): Address => ({
  countryCode,
  cityName: '',
  countyName: '',
  postalCode: '',
  addressLine1: '',
})

const emptyPackage = (): Pkg => ({
  weight: '',
  length: '',
  width: '',
  height: '',
})

function QuickQuote() {
  const navigate = useNavigate()
  const { notify } = useGlobalContext()

  const [packages, setPackages] = useState<Pkg[]>([emptyPackage()])
  const [fromAddress, setFromAddress] = useState<Address>(emptyAddress('NG'))
  const [toAddress, setToAddress] = useState<Address>(emptyAddress(''))

  const [showAdvanced, setShowAdvanced] = useState(false)
  const [deliveryType, setDeliveryType] = useState('')
  const [shipmentType, setShipmentType] = useState('')
  const [insurance, setInsurance] = useState(false)
  const [declaredValue, setDeclaredValue] = useState('')
  const [currency, setCurrency] = useState('NGN')
  const [plannedDate, setPlannedDate] = useState('')

  const [countries, setCountries] = useState<any[]>([])

  const [selectedUser, setSelectedUser] = useState<any>({
    label: 'Optional: search for a customer',
    value: '',
  })
  const [userOptions, setUserOptions] = useState([])
  const [userSearchLoading, setUserSearchLoading] = useState(false)

  const [loading, setLoading] = useState(false)
  const [quote, setQuote] = useState<any>(null)

  useEffect(() => {
    get('utils/get-countries')
      .then((result: any) => {
        if (result.status === 200) {
          setCountries(result.data)
        } else {
          notify('error', result.message)
        }
      })
      .catch(() => notify('error', 'Failed to fetch countries'))
  }, [])

  const fetchUserOptions = (input: string) => {
    if (!input) return
    setUserSearchLoading(true)
    get(`admin/booking-users?search=${input}`)
      .then((result: any) => {
        if (result.status === 200) {
          setUserOptions(
            result.data.map((item: any) => ({
              label: `${item.firstname} ${item.lastname}`,
              value: item._id,
              email: item.email,
            }))
          )
        } else {
          notify('error', result.message)
        }
        setUserSearchLoading(false)
      })
      .catch(() => setUserSearchLoading(false))
  }

  const updatePackage = (index: number, field: keyof Pkg, value: string) => {
    const next = [...packages]
    next[index] = { ...next[index], [field]: value }
    setPackages(next)
  }

  const addPackage = () =>
    setPackages([...packages, emptyPackage()])

  const removePackage = () => {
    if (packages.length <= 1) return
    setPackages(packages.slice(0, -1))
  }

  const handleSubmit = async () => {
    const errors: string[] = []
    packages.forEach((pkg, i) => {
      const w = Number(pkg.weight)
      const l = Number(pkg.length)
      const wd = Number(pkg.width)
      const h = Number(pkg.height)
      if (!w || w <= 0) errors.push(`Package ${i + 1} weight`)
      if (!l || l <= 0) errors.push(`Package ${i + 1} length`)
      if (!wd || wd <= 0) errors.push(`Package ${i + 1} width`)
      if (!h || h <= 0) errors.push(`Package ${i + 1} height`)
    })
    if (!fromAddress.countryCode) errors.push('From country')
    if (!toAddress.countryCode) errors.push('To country')

    // Domestic (NG -> NG) prices are zoned by state — both must be real states
    if (fromAddress.countryCode === 'NG' && toAddress.countryCode === 'NG') {
      if (!STATE_NAMES.includes(fromAddress.countyName))
        errors.push('From state (select a Nigerian state)')
      if (!STATE_NAMES.includes(toAddress.countyName))
        errors.push('To state (select a Nigerian state)')
    }

    if (
      fromAddress.countryCode &&
      toAddress.countryCode &&
      fromAddress.countryCode !== 'NG' &&
      toAddress.countryCode !== 'NG' &&
      !deliveryType
    ) {
      errors.push(
        'Delivery type (required for non-Nigeria to non-Nigeria quotes)'
      )
    }

    if (errors.length) {
      return notify('error', `Required: ${errors.join(', ')}`)
    }

    const stripEmpty = (a: Address) => {
      const out: any = { countryCode: a.countryCode }
      if (a.cityName) out.cityName = a.cityName
      if (a.countyName) out.countyName = a.countyName
      if (a.postalCode) out.postalCode = a.postalCode
      if (a.addressLine1) out.addressLine1 = a.addressLine1
      return out
    }

    const body: any = {
      packages: packages.map((p) => ({
        weight: Number(p.weight),
        length: Number(p.length),
        width: Number(p.width),
        height: Number(p.height),
      })),
      fromAddress: stripEmpty(fromAddress),
      toAddress: stripEmpty(toAddress),
    }

    if (selectedUser.value) body.userId = selectedUser.value
    if (deliveryType) body.deliveryType = deliveryType
    if (shipmentType) body.shipmentType = shipmentType
    if (insurance) body.insurance = true
    if (declaredValue) {
      body.declaredValue = Number(declaredValue)
      body.currency = currency || 'NGN'
    }
    if (plannedDate) {
      body.plannedShippingDateAndTime = new Date(plannedDate).toISOString()
    }

    setLoading(true)
    setQuote(null)
    post('price/customer-quote', body, null)
      .then((result: any) => {
        if (result.status === 200 && result.success !== false) {
          setQuote(result.data)
        } else {
          const msg =
            typeof result.message === 'string'
              ? result.message
              : result.message?.detail || 'Failed to fetch quote'
          notify('error', msg)
        }
        setLoading(false)
      })
      .catch(() => {
        notify('error', 'Failed to fetch quote')
        setLoading(false)
      })
  }

  const fixAllCap = (s: string) =>
    (s || '').charAt(0).toUpperCase() + (s || '').slice(1).toLowerCase()

  const formatNaira = (n: number) =>
    `₦${parseFloat(String(n || 0)).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`

  const handleConvertToBooking = () => {
    if (!quote) return
    const inferred = quote.meta?.inferredDeliveryType as
      | 'export'
      | 'import'
      | 'domestic'
      | undefined
    if (!inferred) {
      notify('error', 'Cannot determine route from quote')
      return
    }
    const prefill = {
      packages: packages.map((p) => ({
        weight: Number(p.weight),
        dimensions: {
          length: Number(p.length),
          width: Number(p.width),
          height: Number(p.height),
        },
      })),
      country: fromAddress.countryCode,
      receiverCountry: toAddress.countryCode,
      pickup_address: fromAddress.addressLine1,
      city: fromAddress.cityName,
      state: fromAddress.countyName,
      postcode: fromAddress.postalCode,
      delivery_address: toAddress.addressLine1,
      delivery_city: toAddress.cityName,
      delivery_state: toAddress.countyName,
      Deliverypostcode: toAddress.postalCode,
      shipment_type:
        shipmentType === 'document'
          ? 'Document'
          : shipmentType === 'package'
          ? 'Package'
          : '',
      value: declaredValue || '',
      insurance,
      selectedUser: selectedUser.value ? selectedUser : null,
    }
    sessionStorage.setItem('quotePrefill', JSON.stringify(prefill))
    navigate(`/admin/${inferred}`)
  }

  const countryOptions = countries.map((c: any) => ({
    label: c.name,
    value: c.isoCode,
  }))

  const findCountryOption = (code: string) => {
    if (!code) return { label: 'Select country', value: '' }
    const c = countries.find((cc: any) => cc.isoCode === code)
    return c
      ? { label: c.name, value: c.isoCode }
      : { label: code, value: code }
  }

  return (
    <div className='quick-quote-page'>
      <div className='qq-wrap'>
        <div className='qq-header'>
          <h2>Quick Quote</h2>
          <p>
            Get a fast shipping estimate. Provide package details and country
            codes; the rest is optional.
          </p>
        </div>

        <div className='qq-grid'>
          <div className='qq-form'>
            <div className='section'>
              <h3>Quote for a customer (optional)</h3>
              <p className='hint'>
                Leave blank for a default-role estimate. Select a customer to
                price using their pricing role.
              </p>
              <Select
                className='dif'
                classNamePrefix='react-select'
                options={userOptions}
                value={selectedUser}
                onChange={(opt) => setSelectedUser(opt)}
                isLoading={userSearchLoading}
                onInputChange={(v) => fetchUserOptions(v)}
                placeholder='Search for a customer'
                isClearable
              />
            </div>

            <div className='section'>
              <h3>Packages</h3>
              {packages.map((pkg, i) => (
                <div key={i} className='pkg-card'>
                  <h4>Package {i + 1}</h4>
                  <div className='input-card'>
                    <p>Weight (KG)</p>
                    <input
                      type='number'
                      min='0.1'
                      step='0.1'
                      value={pkg.weight}
                      onChange={(e) =>
                        updatePackage(i, 'weight', e.target.value)
                      }
                    />
                  </div>
                  <div className='row3'>
                    <div className='input-card'>
                      <p>Length (CM)</p>
                      <input
                        type='number'
                        min='1'
                        value={pkg.length}
                        onChange={(e) =>
                          updatePackage(i, 'length', e.target.value)
                        }
                      />
                    </div>
                    <div className='input-card'>
                      <p>Width (CM)</p>
                      <input
                        type='number'
                        min='1'
                        value={pkg.width}
                        onChange={(e) =>
                          updatePackage(i, 'width', e.target.value)
                        }
                      />
                    </div>
                    <div className='input-card'>
                      <p>Height (CM)</p>
                      <input
                        type='number'
                        min='1'
                        value={pkg.height}
                        onChange={(e) =>
                          updatePackage(i, 'height', e.target.value)
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
              <div className='pkg-actions'>
                <button type='button' onClick={addPackage} className='btn-add'>
                  <Icon icon='mdi:plus' /> Add package
                </button>
                {packages.length > 1 && (
                  <button
                    type='button'
                    onClick={removePackage}
                    className='btn-remove'
                  >
                    <Icon icon='mdi:minus' /> Remove last
                  </button>
                )}
              </div>
            </div>

            <div className='section'>
              <h3>From address (sender)</h3>
              <div className='input-card'>
                <p>Country *</p>
                <Select
                  className='dif'
                  classNamePrefix='react-select'
                  options={countryOptions}
                  value={findCountryOption(fromAddress.countryCode)}
                  onChange={(opt: any) =>
                    setFromAddress({
                      ...fromAddress,
                      countryCode: opt?.value || '',
                    })
                  }
                  placeholder='Select country'
                />
              </div>
              {fromAddress.countryCode && (
                <NewAutoComplete
                  country={fromAddress.countryCode}
                  handeledData={(data: any) =>
                    setFromAddress({
                      ...fromAddress,
                      addressLine1: data.address || '',
                      cityName: data.city || '',
                      postalCode: data.postCode || '',
                      countyName:
                        fromAddress.countryCode === 'NG'
                          ? snapToState(data.state)
                          : data.state || '',
                    })
                  }
                />
              )}
              <div className='row2'>
                <div className='input-card'>
                  <p>City</p>
                  <input
                    type='text'
                    placeholder='City'
                    value={fromAddress.cityName}
                    onChange={(e) =>
                      setFromAddress({
                        ...fromAddress,
                        cityName: e.target.value,
                      })
                    }
                  />
                </div>
                <div className='input-card'>
                  <p>State / County</p>
                  {fromAddress.countryCode === 'NG' ? (
                    <select
                      value={fromAddress.countyName}
                      onChange={(e) =>
                        setFromAddress({
                          ...fromAddress,
                          countyName: e.target.value,
                        })
                      }
                    >
                      <option value=''>Select State</option>
                      {STATE_NAMES.map((name) => (
                        <option value={name} key={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type='text'
                      placeholder='State'
                      value={fromAddress.countyName}
                      onChange={(e) =>
                        setFromAddress({
                          ...fromAddress,
                          countyName: e.target.value,
                        })
                      }
                    />
                  )}
                </div>
              </div>
              <div className='row2'>
                <div className='input-card'>
                  <p>Postal code</p>
                  <input
                    type='text'
                    placeholder='Postal code'
                    value={fromAddress.postalCode}
                    onChange={(e) =>
                      setFromAddress({
                        ...fromAddress,
                        postalCode: e.target.value,
                      })
                    }
                  />
                </div>
                <div className='input-card'>
                  <p>Address line 1</p>
                  <input
                    type='text'
                    placeholder='Street address'
                    value={fromAddress.addressLine1}
                    onChange={(e) =>
                      setFromAddress({
                        ...fromAddress,
                        addressLine1: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <div className='section'>
              <h3>To address (receiver)</h3>
              <div className='input-card'>
                <p>Country *</p>
                <Select
                  className='dif'
                  classNamePrefix='react-select'
                  options={countryOptions}
                  value={findCountryOption(toAddress.countryCode)}
                  onChange={(opt: any) =>
                    setToAddress({
                      ...toAddress,
                      countryCode: opt?.value || '',
                    })
                  }
                  placeholder='Select country'
                />
              </div>
              {toAddress.countryCode && (
                <NewAutoComplete
                  country={toAddress.countryCode}
                  handeledData={(data: any) =>
                    setToAddress({
                      ...toAddress,
                      addressLine1: data.address || '',
                      cityName: data.city || '',
                      postalCode: data.postCode || '',
                      countyName:
                        toAddress.countryCode === 'NG'
                          ? snapToState(data.state)
                          : data.state || '',
                    })
                  }
                />
              )}
              <div className='row2'>
                <div className='input-card'>
                  <p>City</p>
                  <input
                    type='text'
                    placeholder='City'
                    value={toAddress.cityName}
                    onChange={(e) =>
                      setToAddress({
                        ...toAddress,
                        cityName: e.target.value,
                      })
                    }
                  />
                </div>
                <div className='input-card'>
                  <p>State / County</p>
                  {toAddress.countryCode === 'NG' ? (
                    <select
                      value={toAddress.countyName}
                      onChange={(e) =>
                        setToAddress({
                          ...toAddress,
                          countyName: e.target.value,
                        })
                      }
                    >
                      <option value=''>Select State</option>
                      {STATE_NAMES.map((name) => (
                        <option value={name} key={name}>
                          {name}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type='text'
                      placeholder='State'
                      value={toAddress.countyName}
                      onChange={(e) =>
                        setToAddress({
                          ...toAddress,
                          countyName: e.target.value,
                        })
                      }
                    />
                  )}
                </div>
              </div>
              <div className='row2'>
                <div className='input-card'>
                  <p>Postal code</p>
                  <input
                    type='text'
                    placeholder='Postal code'
                    value={toAddress.postalCode}
                    onChange={(e) =>
                      setToAddress({
                        ...toAddress,
                        postalCode: e.target.value,
                      })
                    }
                  />
                </div>
                <div className='input-card'>
                  <p>Address line 1</p>
                  <input
                    type='text'
                    placeholder='Street address'
                    value={toAddress.addressLine1}
                    onChange={(e) =>
                      setToAddress({
                        ...toAddress,
                        addressLine1: e.target.value,
                      })
                    }
                  />
                </div>
              </div>
            </div>

            <div className='section'>
              <button
                type='button'
                className='advanced-toggle'
                onClick={() => setShowAdvanced(!showAdvanced)}
              >
                <Icon
                  icon={
                    showAdvanced
                      ? 'mdi:chevron-down'
                      : 'mdi:chevron-right'
                  }
                />
                Advanced options
              </button>
              {showAdvanced && (
                <div className='advanced'>
                  <div className='row2'>
                    <div className='input-card'>
                      <p>Delivery type</p>
                      <select
                        value={deliveryType}
                        onChange={(e) => setDeliveryType(e.target.value)}
                      >
                        <option value=''>Auto-detect from countries</option>
                        <option value='domestic'>Domestic</option>
                        <option value='export'>Export</option>
                        <option value='import'>Import</option>
                      </select>
                    </div>
                    <div className='input-card'>
                      <p>Shipment type</p>
                      <select
                        value={shipmentType}
                        onChange={(e) => setShipmentType(e.target.value)}
                      >
                        <option value=''>Auto (package)</option>
                        <option value='package'>Package</option>
                        <option value='document'>Document</option>
                      </select>
                    </div>
                  </div>
                  <div className='row2'>
                    <div className='input-card'>
                      <p>Declared value</p>
                      <input
                        type='number'
                        min='0'
                        placeholder='0'
                        value={declaredValue}
                        onChange={(e) => setDeclaredValue(e.target.value)}
                      />
                    </div>
                    <div className='input-card'>
                      <p>Currency</p>
                      <select
                        value={currency}
                        onChange={(e) => setCurrency(e.target.value)}
                      >
                        <option value='NGN'>NGN</option>
                        <option value='USD'>USD</option>
                        <option value='GBP'>GBP</option>
                        <option value='EUR'>EUR</option>
                      </select>
                    </div>
                  </div>
                  <div className='input-card'>
                    <p>Planned shipping date &amp; time</p>
                    <input
                      type='datetime-local'
                      value={plannedDate}
                      onChange={(e) => setPlannedDate(e.target.value)}
                    />
                  </div>
                  <div className='form-check'>
                    <input
                      type='checkbox'
                      id='qqInsurance'
                      checked={insurance}
                      onChange={() => setInsurance(!insurance)}
                    />
                    <label htmlFor='qqInsurance'>
                      Include shipment protection (insurance) in estimate
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className='submit-wrap'>
              {loading ? (
                <FormLoader position='center' />
              ) : (
                <button
                  type='button'
                  className='btn-primary'
                  onClick={handleSubmit}
                >
                  Get estimate
                </button>
              )}
            </div>
          </div>

          <div className='qq-result'>
            {!quote && !loading && (
              <div className='empty'>
                <Icon icon='mdi:calculator-variant' />
                <h3>No quote yet</h3>
                <p>
                  Fill in the package and address details, then press{' '}
                  <b>Get estimate</b>.
                </p>
              </div>
            )}
            {loading && (
              <div className='empty'>
                <FormLoader position='center' />
                <p>Pricing your shipment…</p>
              </div>
            )}
            {quote && (
              <div className='result'>
                <div className='chips'>
                  {quote.meta?.inferredDeliveryType && (
                    <span className='chip route'>
                      Route: {fixAllCap(quote.meta.inferredDeliveryType)}
                    </span>
                  )}
                  {quote.meta?.pricingRole && (
                    <span className='chip role'>
                      Pricing role: {fixAllCap(quote.meta.pricingRole)}
                    </span>
                  )}
                </div>

                <div className='total-card'>
                  <p>Estimated total</p>
                  <h1>{formatNaira(quote.totalPrice)}</h1>
                </div>

                <div className='line-items'>
                  <div className='line'>
                    <span>Shipping cost</span>
                    <span>{formatNaira(quote.shippingCost)}</span>
                  </div>
                  <div className='line'>
                    <span>Shipment protection</span>
                    <span>{formatNaira(quote.insurance)}</span>
                  </div>
                  {quote.breakDown?.extraCharges?.map(
                    (c: any, idx: number) => (
                      <div className='line' key={idx}>
                        <span>{fixAllCap(c.name)}</span>
                        <span>{formatNaira(c.price)}</span>
                      </div>
                    )
                  )}
                  {quote.breakDown?.addOnsCost > 0 && (
                    <div className='line'>
                      <span>Add-ons</span>
                      <span>{formatNaira(quote.breakDown.addOnsCost)}</span>
                    </div>
                  )}
                  {quote.breakDown?.discountAmount > 0 && (
                    <div className='line discount'>
                      <span>Discount</span>
                      <span>
                        -{formatNaira(quote.breakDown.discountAmount)}
                      </span>
                    </div>
                  )}
                  <div className='line'>
                    <span>Gross weight (volumetric inc.)</span>
                    <span>{quote.breakDown?.grossWeight ?? 0} KG</span>
                  </div>
                </div>

                {quote.deliveryCapabilities && (
                  <div className='delivery-meta'>
                    {quote.deliveryCapabilities.totalTransitDays != null && (
                      <div className='line'>
                        <span>Transit days</span>
                        <span>
                          {quote.deliveryCapabilities.totalTransitDays}
                        </span>
                      </div>
                    )}
                    {quote.deliveryCapabilities
                      .estimatedDeliveryDateAndTime && (
                      <div className='line'>
                        <span>Estimated delivery</span>
                        <span>
                          {moment(
                            quote.deliveryCapabilities
                              .estimatedDeliveryDateAndTime
                          ).format('MMM D, YYYY h:mm A')}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                <div className='disclaimer'>
                  <Icon icon='mdi:information-outline' />
                  <p>
                    This is an estimate based on provided package and address
                    details. Final charge may change after actual measurement
                    and chargeable carrier surcharges.
                  </p>
                </div>

                <button
                  type='button'
                  className='btn-primary convert'
                  onClick={handleConvertToBooking}
                >
                  Convert to booking
                  <Icon icon='mdi:arrow-right' />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default QuickQuote
